import { Alert, Button, Card, Field, useToast } from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type JSX, useEffect, useState } from 'react';
import QRCode from 'qrcode';

import { confirmTotpEnrollment, startTotpEnrollment } from '../../account/security-api';
import { useSessionStore } from '../../auth/session-store';
import { useCurrentUser } from '../../auth/use-current-user';

/**
 * The secret never leaves this screen for anywhere but the user's own
 * authenticator: the QR is rendered locally from the `otpauth://` URI, never
 * sent to a third party image service.
 */
export function SecurityPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const markTotpEnrolled = useSessionStore((state) => state.markTotpEnrolled);
  const currentUser = useCurrentUser();
  const totpEnabled = currentUser.data?.totpEnabled === true;

  const enrollment = useQuery({
    queryKey: ['totp-enrollment'],
    queryFn: startTotpEnrollment,
    enabled: currentUser.isSuccess && !totpEnabled,
    staleTime: Infinity,
  });

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (enrollment.data === undefined) {
      return;
    }

    let cancelled = false;

    void QRCode.toDataURL(enrollment.data.enrollmentUri).then((dataUrl) => {
      if (!cancelled) {
        setQrDataUrl(dataUrl);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enrollment.data]);

  const confirm = useMutation({
    mutationFn: (code: string) => confirmTotpEnrollment(code),
    onSuccess: async () => {
      markTotpEnrolled();
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setConfirmed(true);
      toast('success', 'Verificación en dos pasos configurada');
    },
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const code = readText(form, 'code');

    try {
      await confirm.mutateAsync(code);
    } catch {
      // Reported through confirm.isError below; nothing further to do here.
    }
  }

  return (
    <section className="max-w-md">
      <h1 className="text-xl font-semibold text-slate-900">Seguridad</h1>
      <p className="mt-1 text-sm text-slate-500">Configurar verificación en dos pasos</p>

      <div className="mt-6">
        {totpEnabled ? (
          <Alert tone="success">Ya tenés configurada la verificación en dos pasos.</Alert>
        ) : confirmed ? (
          <Alert tone="success">Verificación en dos pasos configurada correctamente.</Alert>
        ) : enrollment.data === undefined ? (
          <p className="text-sm text-slate-500">Preparando tu código...</p>
        ) : (
          <Card>
            <form
              onSubmit={(event) => void handleSubmit(event)}
              className="flex flex-col gap-5"
              noValidate
            >
              {qrDataUrl === null ? null : (
                <img
                  src={qrDataUrl}
                  alt="Código QR para configurar la verificación en dos pasos"
                  className="size-48 self-center"
                />
              )}

              <div>
                <p className="text-sm font-medium text-slate-900">¿No podés escanear el código?</p>
                <p className="mt-1 break-all text-sm text-slate-600">{enrollment.data.secret}</p>
              </div>

              {confirm.isError ? (
                <Alert tone="danger">Código inválido. Probá de nuevo.</Alert>
              ) : null}

              <Field
                label="Código de verificación"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                hint="Seis dígitos de tu aplicación de autenticación"
                required
                autoFocus
              />

              <Button type="submit" loading={confirm.isPending}>
                Confirmar
              </Button>
            </form>
          </Card>
        )}
      </div>
    </section>
  );
}

function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}
